/**
 * Parent-only view for a specific child's day.
 * No child device logic, no child UI, no conditional rendering.
 */
import { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTaskItems, usePointsToday } from "@/hooks/useTaskItems";
import { useRecurringTasks, type RecurringTaskItem } from "@/hooks/useRecurringTasks";

import { useHomework } from "@/hooks/useHomework";
import { useReminders } from "@/hooks/useReminders";
import { useTimer } from "@/hooks/useTimer";
import { useAuth } from "@/hooks/useAuth";
import { useFamily } from "@/hooks/useFamily";
import { useCustodyForDate } from "@/hooks/useTodayCustody";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useImportQuota } from "@/hooks/useImportQuota";
import { useAllChildren } from "@/hooks/useAllChildren";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { CircularTimer } from "@/components/CircularTimer";
import { UpgradeHint } from "@/components/UpgradeHint";
import { TaskItemDetailSheet } from "@/components/TaskItemDetailSheet";
import { SchoolPlanImport } from "@/components/SchoolPlanImport";
import { AddHomeworkSheet } from "@/components/AddHomeworkSheet";
import { HomeworkSection } from "@/components/HomeworkSection";
import { ReminderSection } from "@/components/ReminderSection";
import { AddReminderSheet } from "@/components/AddReminderSheet";
import { SchoolPlanViewer } from "@/components/SchoolPlanViewer";
import { ChildViewSkeleton } from "@/components/skeletons/PageSkeletons";
import ChildDayView from "@/pages/child/ChildDayView";
import ChildCalendar from "@/pages/parent/ChildCalendar";
import { ParentChildListHeader } from "@/pages/parent/ParentChildListHeader";
import { formatDateLong, getDateString } from "@/lib/family-date";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Bell, BookOpen, ChevronDown, ChevronRight, Plus, Upload, CalendarDays, AlertTriangle, ListTodo, MessageSquare, TrendingUp, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

type ViewState =
  | { type: "list" }
  | { type: "timer"; itemId: string; title: string; description?: string | null }
  | { type: "done"; itemId: string; title: string }
  | { type: "improvement"; itemId: string; title: string };

export default function ParentChildView() {
  const { id: childId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { timezone, familyId: authFamilyId } = useFamily();
  // Entitlements for this child's family (so history/Plus gate is per child when viewing cross-family)
  const { data: child } = useQuery({
    queryKey: ["child", childId, "parent"],
    queryFn: async () => {
      if (!childId) return null;
      const { data, error } = await supabase.from("children").select("*").eq("id", childId).single();
      if (error) throw error;
      return data;
    },
    enabled: !!childId,
  });
  const childFamilyId = child?.family_id ?? authFamilyId;
  const { hasPlus, hasTogether, maxHistoryDays } = useEntitlements(childFamilyId);
  const { used: importQuotaUsed, limit: importQuotaLimit, remaining: importQuotaRemaining } = useImportQuota(childFamilyId);
  const { hasFlag } = useFeatureFlags();
  const { children: allChildren } = useAllChildren();
  const queryClient = useQueryClient();
  const [view, setView] = useState<ViewState>({ type: "list" });
  const [improvementNote, setImprovementNote] = useState("");
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [dateOffset, setDateOffset] = useState(0);
  /** "date" = single day (dateOffset); "all" = Alle oppgaver (date-independent) */
  const [taskViewMode, setTaskViewMode] = useState<"date" | "all">("date");
  /** Søk i oppgaver (kun i Alle oppgaver) */
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const [taskSearchDebounced, setTaskSearchDebounced] = useState("");
  /** Seksjonsgrenser for Alle oppgaver: Vis flere øker limit */
  const [visibleLimitBySection, setVisibleLimitBySection] = useState({ forfalt: 5, active: 10, fullfort: 5 });
  const [fullfortCollapsed, setFullfortCollapsed] = useState(true);
  const remainingSecondsRef = useRef<Record<string, number>>({});
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [activeItemDuration, setActiveItemDuration] = useState(600);
  const [showPlanImport, setShowPlanImport] = useState(false);
  const [showAddHomework, setShowAddHomework] = useState(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [showStarDetailDialog, setShowStarDetailDialog] = useState(false);

  /** Lister | Kalender tab; synced with ?tab=calendar */
  const [viewTab, setViewTab] = useState<"list" | "calendar">("list");
  const [calendarWeekOffset, setCalendarWeekOffset] = useState(0);
  const [openCalendarAdd, setOpenCalendarAdd] = useState(false);

  /** Collapse state per section */
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) => setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Sync viewTab from URL ?tab=calendar
  useEffect(() => {
    const tab = searchParams.get("tab");
    setViewTab(tab === "calendar" ? "calendar" : "list");
  }, [searchParams]);

  // Open sheet from dashboard ?open=task|homework|reminder
  useEffect(() => {
    const open = searchParams.get("open");
    if (!open || !childId) return;
    if (open === "task") setShowCreateSheet(true);
    else if (open === "homework") setShowPlanImport(true);
    else if (open === "reminder") setShowAddReminder(true);
    setSearchParams({}, { replace: true });
  }, [childId, searchParams, setSearchParams]);
  // Debounce task search (200ms) – kun brukt i Alle oppgaver
  useEffect(() => {
    const t = setTimeout(() => setTaskSearchDebounced(taskSearchQuery), 200);
    return () => clearTimeout(t);
  }, [taskSearchQuery]);

  // Ved aktivt søk: ekspander minst én kategori (Fullført) så treff vises
  useEffect(() => {
    if (taskSearchDebounced.trim()) setFullfortCollapsed(false);
  }, [taskSearchDebounced]);

  // data-role handled by AppShell

  const viewedDate = new Date();
  viewedDate.setDate(viewedDate.getDate() + dateOffset);
  const viewedDateString = getDateString(viewedDate, timezone);
  const today = getDateString(new Date(), timezone);
  const isToday = viewedDateString === today;

  // Uke-label for Kalender-tab (samme format som ChildCalendar)
  const calendarWeekLabel = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMon + calendarWeekOffset * 7);
    const fri = new Date(monday);
    fri.setDate(monday.getDate() + 4);
    const monDay = monday.getDate();
    const friDay = fri.getDate();
    const monMonth = monday.toLocaleString("nb-NO", { month: "short" });
    const friMonth = fri.toLocaleString("nb-NO", { month: "short" });
    if (monday.getMonth() === fri.getMonth()) {
      return `${monDay}.–${friDay}. ${monMonth}`;
    }
    return `${monDay}. ${monMonth} – ${friDay}. ${friMonth}`;
  }, [calendarWeekOffset]);

  const isCurrentCalendarWeek = calendarWeekOffset === 0;
  const calendarWeekLabelDisplay = isCurrentCalendarWeek ? "Denne uken" : calendarWeekLabel;

  // ── Family members for creator name (child's family) ────────────────
  const { data: familyMembers = [] } = useQuery({
    queryKey: ["family-members", childFamilyId],
    queryFn: async () => {
      if (!childFamilyId) return [];
      const { data, error } = await supabase
        .from("family_members")
        .select("user_id, display_name, child_facing_name")
        .eq("family_id", childFamilyId);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!childFamilyId,
  });

  function getCreatorName(createdBy: string | null | undefined): string | null {
    if (!createdBy) return null;
    const member = familyMembers.find((m: any) => m.user_id === createdBy);
    if (member) return member.display_name || member.child_facing_name || "Forelder";
    return "Ukjent";
  }

  const tasks = useTaskItems(childId, null, taskViewMode === "all" ? "all" : viewedDateString);
  const { data: pointsToday = 0 } = usePointsToday(childId);
  const recurring = useRecurringTasks(childId, taskViewMode === "all" ? "all" : viewedDateString);
  const { custodyMap, isWithOtherParent } = useCustodyForDate(viewedDateString);
  const childIsWithOther = childId ? isWithOtherParent(childId) : false;
  const custodialParentId = childId ? custodyMap.get(childId) : undefined;

  // Determine which items need approval (created by other parent, not yet approved, and there IS a custody record)
  function needsApproval(item: any): boolean {
    if (!custodialParentId) return false; // no custody plan = no approval needed
    if (!item.created_by) return false;
    if (item.created_by === custodialParentId) return false; // created by custodial parent
    if (item.approved_by === custodialParentId) return false; // already approved
    return true;
  }
  const { items, isLoading, canAdd: focusCanAdd } = tasks;
  const isFutureOrToday = viewedDateString >= today;
  const isHistoryGated = !hasPlus && dateOffset < -maxHistoryDays;
  const canAdd = (taskViewMode === "all" || isFutureOrToday) && focusCanAdd && !childIsWithOther;
  const canGoBack = hasPlus || dateOffset > -maxHistoryDays;

  // Alle oppgaver: filtrert liste og inndeling i seksjoner (Forfalt, Aktive/Kommende, Fullført)
  const taskSearchLower = taskSearchDebounced.trim().toLowerCase();
  const filteredItemsForAll = useMemo(() => {
    if (!taskSearchLower) return items;
    return items.filter((i: any) => (i.title || "").toLowerCase().includes(taskSearchLower));
  }, [items, taskSearchLower]);
  const sectionsForAll = useMemo(() => {
    const forfalt: any[] = [];
    const active: any[] = [];
    const fullfort: any[] = [];
    for (const item of filteredItemsForAll) {
      if (item.status === "done") fullfort.push(item);
      else if ((item.date || "") < today) forfalt.push(item);
      else active.push(item);
    }
    return { forfalt, active, fullfort };
  }, [filteredItemsForAll, today]);
  const showAllSearchResults = !!taskSearchDebounced.trim();
  const effectiveLimits = showAllSearchResults
    ? { forfalt: 999, active: 999, fullfort: 999 }
    : visibleLimitBySection;

  // Urgent homework: incomplete with due_date within 2 days
  const { items: allHomework = [] } = useHomework(childId);
  const { items: allReminders = [] } = useReminders(childId);
  const urgentHomework = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const threshold = new Date(now);
    threshold.setDate(threshold.getDate() + 2);
    return allHomework.filter((hw) => {
      if (hw.completed_at) return false;
      if (!hw.due_date) return false;
      const due = new Date(hw.due_date + "T00:00:00");
      return due <= threshold && due >= now;
    });
  }, [allHomework]);

  const siblingOptions = useMemo(() =>
    allChildren
      .filter((c) => c.family_id === child?.family_id && c.id !== childId)
      .map((c) => ({ id: c.id, name: c.name, avatar_emoji: c.avatar_emoji })),
    [allChildren, child?.family_id, childId]
  );

  /** Har barnet noen oppgaver i det hele tatt (alle dager)? Aktive brukere får "Ingen oppgaver" i stedet for eksempelkort. */
  const { data: hasAnyTasksInDb } = useQuery({
    queryKey: ["child-has-any-tasks", childId],
    queryFn: async () => {
      if (!childId) return false;
      const { data, error } = await supabase
        .from("task_items")
        .select("id")
        .eq("child_id", childId)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!childId,
  });

  /** Har barnet noen lekser i DB? Aktive brukere får "Ingen lekser" i stedet for eksempelkort. */
  const { data: hasAnyHomeworkInDb } = useQuery({
    queryKey: ["child-has-any-homework", childId],
    queryFn: async () => {
      if (!childId) return false;
      const { data, error } = await supabase
        .from("homework" as any)
        .select("id")
        .eq("child_id", childId)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!childId,
  });

  // Point transactions for star detail dialog (when opened)
  const { data: pointTransactions = [] } = useQuery({
    queryKey: ["point-transactions", childId],
    queryFn: async () => {
      if (!childId) return [];
      const { data, error } = await supabase
        .from("point_transactions")
        .select("amount, created_at")
        .eq("child_id", childId)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!childId && showStarDetailDialog,
  });

  const starDetailByDate = useMemo(() => {
    const todayStr = getDateString(new Date(), timezone);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = getDateString(yesterday, timezone);
    const byDate: Record<string, number> = {};
    for (const row of pointTransactions) {
      const dateStr = getDateString(new Date(row.created_at), timezone);
      byDate[dateStr] = (byDate[dateStr] ?? 0) + row.amount;
    }
    const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a)).slice(0, 14);
    return sortedDates.map((dateStr) => {
      const label =
        dateStr === todayStr
          ? "I dag"
          : dateStr === yesterdayStr
            ? "I går"
            : formatDateLong(dateStr + "T12:00:00", timezone);
      return { dateStr, label, amount: byDate[dateStr]! };
    });
  }, [pointTransactions, timezone]);

  const timer = useTimer(activeItemDuration);

  // Tick every second to update elapsed time for active items
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const hasActive = items.some((i: any) => i.status === "active");
    if (!hasActive) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [items]);

  function formatElapsed(startedAt: string | null): string {
    if (!startedAt) return "";
    const elapsed = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  if (isLoading && !child) return <ChildViewSkeleton />;

  // ── Handlers ──────────────────────────────────────
  function handleStart(itemId: string, title: string, durationSeconds: number = 600, description?: string | null) {
    tasks.startItem.mutate(itemId, {
      onSuccess: () => {
        setActiveItemDuration(durationSeconds);
        setView({ type: "timer", itemId, title, description });
        const saved = remainingSecondsRef.current[itemId];
        timer.start(saved && saved > 0 ? saved : durationSeconds);
      },
    });
  }

  function handleResume(itemId: string, title: string, durationSeconds: number = 600, description?: string | null) {
    setActiveItemDuration(durationSeconds);
    setView({ type: "timer", itemId, title, description });
    const saved = remainingSecondsRef.current[itemId];
    timer.start(saved && saved > 0 ? saved : durationSeconds);
  }

  function handlePause(itemId: string) {
    remainingSecondsRef.current[itemId] = timer.secondsLeft;
    timer.pause();
    setView({ type: "list" });
  }

  function handleComplete(itemId: string, title: string) {
    setView({ type: "improvement", itemId, title });
  }

  function handleSaveImprovement(itemId: string) {
    const item = items.find((i: any) => i.id === itemId);
    const cat = item?.category;
    const pts = cat && cat !== "Annet" ? 1 : 0;
    tasks.completeItem.mutate(
      { itemId, improvementNote: improvementNote.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(pts > 0 ? "Fullført! +1 poeng ⭐" : "Fullført!");
          setImprovementNote("");
          timer.reset();
          setView({ type: "list" });
        },
      }
    );
  }

  function handleSkipImprovement(itemId: string) {
    const item = items.find((i: any) => i.id === itemId);
    const cat = item?.category;
    const pts = cat && cat !== "Annet" ? 1 : 0;
    tasks.completeItem.mutate(
      { itemId },
      {
        onSuccess: () => {
          toast.success(pts > 0 ? "Fullført! +1 poeng ⭐" : "Fullført!");
          timer.reset();
          setView({ type: "list" });
        },
      }
    );
  }

  // ── Timer view ──────────────────────────────────────
  if (view.type === "timer") {
    return <ParentTimerView view={view} timer={timer} setView={setView} onPause={handlePause} onComplete={handleComplete} remainingSecondsRef={remainingSecondsRef} durationSeconds={activeItemDuration} />;
  }

  // ── Done view ──────────────────────────────────────
  if (view.type === "done") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
        <div className="text-6xl">✅</div>
        <h2 className="text-2xl font-display font-bold">Fullført</h2>
        <p className="text-muted-foreground text-center">«{view.title}» er ferdig.</p>
        <Button onClick={() => handleComplete(view.itemId, view.title)} className="w-full max-w-xs">
          Fortsett
        </Button>
      </div>
    );
  }

  // ── Improvement view ──────────────────────────────
  if (view.type === "improvement") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
        <div className="text-4xl">💭</div>
        <h2 className="text-xl font-display font-bold text-center">Forbedringsnotat</h2>
        <p className="text-sm text-muted-foreground text-center">
          Hva kunne barnet gjort annerledes?
        </p>
        <Textarea
          value={improvementNote}
          onChange={(e) => setImprovementNote(e.target.value)}
          placeholder="Skriv her… (valgfritt)"
          className="w-full max-w-xs"
          rows={3}
        />
        <div className="flex gap-2 w-full max-w-xs">
          <Button variant="outline" className="flex-1" onClick={() => handleSkipImprovement(view.itemId)}>
            Hopp over
          </Button>
          <Button className="flex-1" onClick={() => handleSaveImprovement(view.itemId)}>
            Lagre
          </Button>
        </div>
      </div>
    );
  }

  // Visningstall for header/badge (inkl. eksempelkort når ingen reelle)
  const totalTaskCount = items.length + recurring.items.length;
  const taskDisplayCount =
    totalTaskCount > 0 ? totalTaskCount : totalTaskCount === 0 && !isLoading && hasAnyTasksInDb === false ? 3 : 0;
  const homeworkDisplayCount =
    allHomework.length > 0 ? allHomework.length : allHomework.length === 0 && hasAnyHomeworkInDb === false ? 2 : 0;

  // ── List view ──────────────────────────────────────
  return (
    <div className="min-h-full bg-background pb-6">
      <ParentChildListHeader
        childId={childId ?? ""}
        childName={child?.name ?? null}
        avatarEmoji={child?.avatar_emoji ?? null}
        pointsToday={pointsToday}
        activeTab={viewTab}
        onTabChange={(tab) => {
          setViewTab(tab);
          setSearchParams(tab === "calendar" ? { tab: "calendar" } : {}, { replace: true });
        }}
        taskViewMode={taskViewMode}
        onTaskViewModeChange={setTaskViewMode}
        dateOffset={dateOffset}
        onDateOffsetChange={(offset) => {
          setDateOffset(offset);
          setTaskViewMode("date");
        }}
        weekOffset={viewTab === "calendar" ? calendarWeekOffset : undefined}
        onWeekOffsetChange={viewTab === "calendar" ? setCalendarWeekOffset : undefined}
        weekLabel={viewTab === "calendar" ? calendarWeekLabelDisplay : undefined}
        canAdd={canAdd}
        hasPlus={hasPlus}
        hasTogether={hasTogether}
        maxHistoryDays={maxHistoryDays}
        onOpenCreateTask={() => setShowCreateSheet(true)}
        onOpenCalendar={() => {
          setViewTab("calendar");
          setSearchParams({ tab: "calendar" }, { replace: true });
          setOpenCalendarAdd(true);
        }}
        onOpenAddReminder={() => setShowAddReminder(true)}
        onOpenAddHomework={() => setShowAddHomework(true)}
        onOpenPlanImport={() => setShowPlanImport(true)}
        onOpenCalendarAdd={viewTab === "calendar" ? () => setOpenCalendarAdd(true) : undefined}
        onOpenStarDetail={() => setShowStarDetailDialog(true)}
        importQuotaUsed={hasPlus ? undefined : importQuotaUsed}
        importQuotaLimit={hasPlus ? undefined : importQuotaLimit}
        importQuotaRemaining={hasPlus ? undefined : importQuotaRemaining}
      />
      {viewTab === "calendar" ? (
        <ChildCalendar
          embedded
          weekOffset={calendarWeekOffset}
          onWeekOffsetChange={setCalendarWeekOffset}
          openAdd={openCalendarAdd}
          onAddClosed={() => setOpenCalendarAdd(false)}
        />
      ) : (
        <>
      {/* Stjernestatus-dialog */}
      <Dialog open={showStarDetailDialog} onOpenChange={setShowStarDetailDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">
              Stjernestatus {child?.avatar_emoji && <span>{child.avatar_emoji}</span>} {child?.name}
            </DialogTitle>
            <DialogDescription className="text-center">
              Hvor mange stjerner barnet har oppnådd og når.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-center">
              <p className="text-3xl font-bold text-accent-foreground">⭐ {pointsToday}</p>
              <p className="text-sm text-muted-foreground">stjerner totalt</p>
            </div>
            {starDetailByDate.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Siste dager</p>
                <ul className="space-y-1.5 text-sm">
                  {starDetailByDate.map(({ dateStr, label, amount }) => (
                    <li key={dateStr} className="flex justify-between items-center">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium">+{amount} ⭐</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!hasPlus && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  Med Flyt Pluss får du enda mer innsikt med rapporter, vanetrender og utvikling over tid.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 text-xs"
                  onClick={() => {
                    setShowStarDetailDialog(false);
                    navigate("/reports");
                  }}
                >
                  <TrendingUp className="h-3.5 w-3.5" />
                  Se rapporter og Flyt Pluss
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <div className="px-4 pt-0">
        <div className="mx-auto max-w-sm space-y-2">

        {/* Urgent homework alert */}
        {urgentHomework.length > 0 && !childIsWithOther && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700/40 px-3 py-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
              {urgentHomework.length === 1
                ? "1 lekse har frist innen 2 dager"
                : `${urgentHomework.length} lekser har frist innen 2 dager`}
            </p>
          </div>
        )}

        {/* Velkomst for ny bruker: ingen oppgaver/lekser i dag og har aldri lagt inn noen */}
        {!childIsWithOther &&
          items.length === 0 &&
          allHomework.length === 0 &&
          isToday &&
          !isLoading &&
          hasAnyTasksInDb === false &&
          hasAnyHomeworkInDb === false && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4 space-y-3">
              <p className="font-semibold text-sm">
                Kom i gang med {child?.name ?? "barnet"} sin plan
              </p>
              <p className="text-xs text-muted-foreground">
                Legg til oppgaver eller lekser her – så ser barnet dem på sin skjerm og kan kryss av når noe er gjort. Du kan også legge til hendelser i kalenderen.
              </p>
              <div className="flex flex-col gap-2">
                <Button className="w-full gap-2" onClick={() => setShowCreateSheet(true)}>
                  <ListTodo className="h-4 w-4" />
                  Legg til første oppgave
                </Button>
                <Button variant="outline" className="w-full gap-2" onClick={() => setShowAddHomework(true)}>
                  <BookOpen className="h-4 w-4" />
                  Legg til lekse
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Oppgaver-seksjon – samme oppsett som Lekser og Påminnelser */}
        <div>
          <button
            type="button"
            className="text-lg font-display font-semibold flex items-center gap-2 pb-2 w-full text-left"
            onClick={() => toggleSection("oppgaver")}
          >
            <ChevronRight className={cn("h-4 w-4 shrink-0 transition-transform text-muted-foreground", !collapsedSections.oppgaver && "rotate-90")} />
            <ListTodo className="h-5 w-5 text-primary shrink-0" />
            Oppgaver
            {collapsedSections.oppgaver && <Badge variant="secondary" className="text-xs">{taskDisplayCount}</Badge>}
          </button>
        {!collapsedSections.oppgaver && (
          <>
        {taskViewMode === "all" && !childIsWithOther && (
          <div className="pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Søk i oppgaver…"
                value={taskSearchQuery}
                onChange={(e) => setTaskSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm bg-muted/50 border-0"
              />
            </div>
          </div>
        )}
        {taskViewMode === "date" && isHistoryGated && (
          <div className="relative rounded-xl overflow-hidden">
            <div className="blur-sm pointer-events-none opacity-50 space-y-3 py-4">
              <Card><CardContent className="p-4"><div className="h-4 w-3/4 rounded bg-muted" /><div className="h-3 w-1/2 rounded bg-muted mt-2" /></CardContent></Card>
              <Card><CardContent className="p-4"><div className="h-4 w-2/3 rounded bg-muted" /><div className="h-3 w-1/3 rounded bg-muted mt-2" /></CardContent></Card>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <UpgradeHint
                title="Se hele historikken"
                description="Med Flyt Pluss kan du se tilbake mer enn 7 dager."
                targetPlan="plus"
                useCheckout
              />
            </div>
          </div>
        )}

        {childIsWithOther ? (
          <div className="text-center py-8 space-y-2">
            <p className="text-lg">🏠</p>
            <p className="text-sm text-muted-foreground">
              Barnet er hos medforelder {isToday ? "i dag" : "denne dagen"}.
            </p>
            <p className="text-xs text-muted-foreground">
              Oppgaver er kun synlige for forelderen med samvær.
            </p>
          </div>
        ) : taskViewMode === "all" ? (
          /* Alle oppgaver: seksjoner med Vis flere */
          (() => {
            const renderTaskCard = (item: any) => (
              <Card
                key={item.id}
                className={cn("cursor-pointer active:bg-muted/60 transition-colors", needsApproval(item) && "border-amber-400/50")}
                onClick={() => setSelectedItem(item)}
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={cn("font-medium", item.status === "done" && "line-through text-muted-foreground")}>
                        {item.title}
                      </p>
                      {needsApproval(item) && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">
                          Trenger godkjenning
                        </Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">📝 {item.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.status === "pending" && "Ikke startet"}
                      {item.status === "active" && `Pågår… · ⏱️ ${formatElapsed(item.started_at)} / ${Math.round(item.duration_seconds / 60)} min`}
                      {item.status === "done" && "Ferdig"}
                      {item.status !== "active" && item.duration_seconds !== 600 && ` · ${Math.round(item.duration_seconds / 60)} min`}
                    </p>
                    {item.status === "done" && item.improvement_note && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">💭 {item.improvement_note}</p>
                    )}
                    {item.date && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        📅 {formatDateLong(item.date + "T12:00:00", timezone)}
                      </p>
                    )}
                  </div>
                  {item.status === "done" && <Checkbox checked disabled className="h-6 w-6 rounded-full" />}
                  {item.status !== "done" && <span className="text-xs text-muted-foreground">Trykk for detaljer →</span>}
                </CardContent>
              </Card>
            );
            const Section = ({
              title,
              list,
              limit,
              onShowMore,
              collapsed,
              onToggleCollapsed,
            }: {
              title: string;
              list: any[];
              limit: number;
              onShowMore: () => void;
              collapsed?: boolean;
              onToggleCollapsed?: () => void;
            }) => {
              const visible = list.slice(0, limit);
              const rest = list.length - limit;
              if (list.length === 0) return null;
              const Header = onToggleCollapsed ? "button" : "div";
              return (
                <div className="space-y-2">
                  <Header
                    type={onToggleCollapsed ? "button" : undefined}
                    className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground w-full text-left"
                    onClick={onToggleCollapsed}
                  >
                    {onToggleCollapsed && (
                      <ChevronRight className={cn("h-4 w-4 shrink-0 transition-transform", !collapsed && "rotate-90")} />
                    )}
                    <span>{title}</span>
                    <span className="text-xs">({list.length})</span>
                  </Header>
                  {(!collapsed || collapsed === undefined) && (
                    <>
                      <div className="space-y-3">
                        {visible.map((item: any) => renderTaskCard(item))}
                      </div>
                      {rest > 0 && (
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-foreground underline"
                          onClick={onShowMore}
                        >
                          Vis flere ({rest})
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            };
            return (
              <div className="space-y-6">
                <Section
                  title="Forfalt"
                  list={sectionsForAll.forfalt}
                  limit={effectiveLimits.forfalt}
                  onShowMore={() => setVisibleLimitBySection((p) => ({ ...p, forfalt: p.forfalt + 10 }))}
                />
                <Section
                  title="Aktive / Kommende"
                  list={sectionsForAll.active}
                  limit={effectiveLimits.active}
                  onShowMore={() => setVisibleLimitBySection((p) => ({ ...p, active: p.active + 10 }))}
                />
                <Section
                  title="Fullført"
                  list={sectionsForAll.fullfort}
                  limit={effectiveLimits.fullfort}
                  onShowMore={() => setVisibleLimitBySection((p) => ({ ...p, fullfort: p.fullfort + 10 }))}
                  collapsed={fullfortCollapsed}
                  onToggleCollapsed={() => setFullfortCollapsed((c) => !c)}
                />
                {filteredItemsForAll.length === 0 && !isLoading && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {taskSearchLower ? "Ingen treff for søket." : "Ingen oppgaver."}
                  </p>
                )}
              </div>
            );
          })()
        ) : (
          <div className="space-y-4">
            {items.map((item: any) => (
              <Card
                key={item.id}
                className={`cursor-pointer active:bg-muted/60 transition-colors ${needsApproval(item) ? "border-amber-400/50" : ""}`}
                onClick={() => setSelectedItem(item)}
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${item.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                        {item.title}
                      </p>
                      {needsApproval(item) && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">
                          Trenger godkjenning
                        </Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        📝 {item.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.status === "pending" && "Ikke startet"}
                      {item.status === "active" && `Pågår… · ⏱️ ${formatElapsed(item.started_at)} / ${Math.round(item.duration_seconds / 60)} min`}
                      {item.status === "done" && "Ferdig"}
                      {item.status !== "active" && item.duration_seconds !== 600 && ` · ${Math.round(item.duration_seconds / 60)} min`}
                    </p>
                    {item.status === "done" && item.improvement_note && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">💭 {item.improvement_note}</p>
                    )}
                  </div>
                  {item.status === "done" && <Checkbox checked disabled className="h-6 w-6 rounded-full" />}
                  {item.status !== "done" && <span className="text-xs text-muted-foreground">Trykk for detaljer →</span>}
                </CardContent>
              </Card>
            ))}

            {/* Recurring task occurrences */}
            {recurring.items.map((rItem) => (
              <Card
                key={`rec-${rItem.id}`}
                className="cursor-pointer active:bg-muted/60 transition-colors border-primary/20"
                onClick={() => setSelectedItem({
                  ...rItem,
                  id: rItem.id,
                  title: rItem.title,
                  description: rItem.description,
                  category: rItem.category,
                  duration_seconds: rItem.durationSeconds,
                  completion_points: rItem.completionPoints,
                  status: rItem.status,
                  started_at: rItem.startedAt,
                  done_at: rItem.doneAt,
                  improvement_note: rItem.improvementNote,
                  date: rItem.dueDate,
                  isRecurring: true,
                  _recurringOccurrenceId: rItem.id,
                  _recurringTemplateId: rItem.templateId,
                })}
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${rItem.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                        {rItem.title}
                      </p>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/40 text-primary">
                        🔁 Gjentagende
                      </Badge>
                    </div>
                    {rItem.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">📝 {rItem.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {rItem.status === "pending" && "Ikke startet"}
                      {rItem.status === "done" && "Ferdig"}
                      {rItem.durationSeconds !== 600 && ` · ${Math.round(rItem.durationSeconds / 60)} min`}
                    </p>
                    {rItem.status === "done" && rItem.improvementNote && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">💭 {rItem.improvementNote}</p>
                    )}
                  </div>
                  {rItem.status === "done" && <Checkbox checked disabled className="h-6 w-6 rounded-full" />}
                  {rItem.status !== "done" && <span className="text-xs text-muted-foreground">Trykk for detaljer →</span>}
                </CardContent>
              </Card>
            ))}

            {items.length === 0 && recurring.items.length === 0 && !isLoading && (
              hasAnyTasksInDb !== false ? (
                <p className="text-xs text-muted-foreground py-1">Ingen i dag</p>
              ) : (
                <div className="space-y-4">
                  {[
                    { title: "F.eks. Rydde rommet", sub: "Ikke startet · 10 min" },
                    { title: "F.eks. Lese 15 min", sub: "Ikke startet · 15 min" },
                    { title: "F.eks. Pusse tenner", sub: "Ikke startet · 5 min" },
                  ].map((p, idx) => (
                    <Card key={idx} className="border-dashed opacity-75 pointer-events-none">
                      <CardContent className="flex items-center justify-between p-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-muted-foreground">{p.title}</p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{p.sub}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">Trykk for detaljer →</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            )}
          </div>
        )}
          </>
        )}
        </div>

        {/* Lekser-seksjon */}
        {childId && !childIsWithOther && (
          <div>
            <button
              type="button"
              className="text-lg font-display font-semibold flex items-center gap-2 pb-2 w-full text-left"
              onClick={() => toggleSection("lekser")}
            >
              <ChevronRight className={cn("h-4 w-4 shrink-0 transition-transform text-muted-foreground", !collapsedSections.lekser && "rotate-90")} />
              <BookOpen className="h-5 w-5 text-primary shrink-0" />
              Lekser
              {collapsedSections.lekser && <Badge variant="secondary" className="text-xs">{homeworkDisplayCount}</Badge>}
            </button>
            {!collapsedSections.lekser && (
              <>
                <HomeworkSection
                  childId={childId}
                  isParent
                  interactive
                  canImport={hasPlus || hasTogether}
                  headerInvisible
                />
                {allHomework.length === 0 && (
                  <div className="space-y-3">
                    {hasAnyHomeworkInDb !== false ? (
                      <p className="text-xs text-muted-foreground py-1">Ingen i dag</p>
                    ) : (
                      <>
                        <div className="space-y-3">
                          {[
                            { title: "F.eks. Matte s. 42–45", frist: "Frist fredag" },
                            { title: "F.eks. Les kapittel 3 i Norsk", frist: "Frist neste uke" },
                          ].map((p, idx) => (
                            <Card key={idx} className="border-dashed opacity-75 pointer-events-none transition-all">
                              <CardContent className="p-3">
                                <div className="flex items-start gap-3 cursor-pointer rounded-md -m-1 p-1">
                                  <div className="mt-0.5 shrink-0">
                                    <div className="h-6 w-6 rounded-lg bg-secondary flex items-center justify-center">
                                      <BookOpen className="h-3.5 w-3.5 text-primary" />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-muted-foreground">{p.title}</p>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                                        {p.frist}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">Legg til lekser via Legg til-menyen over.</p>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* School plan viewer – right after homework for visibility */}
        {childId && !childIsWithOther && (
          <SchoolPlanViewer childId={childId} />
        )}

        {/* Påminnelser-seksjon */}
        {childId && !childIsWithOther && (
          <div>
            <button
              type="button"
              className="text-lg font-display font-semibold flex items-center gap-2 pb-2 w-full text-left"
              onClick={() => toggleSection("paminnelser")}
            >
              <ChevronRight className={cn("h-4 w-4 shrink-0 transition-transform text-muted-foreground", !collapsedSections.paminnelser && "rotate-90")} />
              <Bell className="h-5 w-5 text-primary shrink-0" />
              Påminnelser
              {collapsedSections.paminnelser && allReminders.length > 0 && <Badge variant="secondary" className="text-xs">{allReminders.length}</Badge>}
            </button>
            {!collapsedSections.paminnelser && (
              <ReminderSection childId={childId} isParent date={viewedDateString} headerInvisible />
            )}
          </div>
        )}

        {/* Beta features */}
        {canAdd && hasFlag("beta_features") && (
          <details className="group rounded-xl border bg-card">
            <summary className="flex items-center justify-between cursor-pointer px-4 py-3 text-sm font-medium text-muted-foreground select-none list-none [&::-webkit-details-marker]:hidden">
              <div className="flex items-center gap-2">
                <span>🧪 Beta-funksjoner</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Ny</Badge>
              </div>
              <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-4 pb-4 space-y-2">
              {siblingOptions.length > 0 && (
                <CopyFromSiblingButton
                  childId={childId!}
                  familyId={childFamilyId!}
                  siblings={siblingOptions}
                  timezone={timezone}
                />
              )}
              <Button
                variant="ghost"
                className="w-full rounded-xl text-sm gap-2 text-muted-foreground"
                onClick={() => navigate(`/parent/child/${childId}/preview`)}
              >
                👀 Se barnets visning
              </Button>
            </div>
          </details>
        )}

        </div>
      </div>

      {/* Detail sheet */}
      <TaskItemDetailSheet
        item={selectedItem}
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        isParent
        creatorName={getCreatorName((selectedItem as any)?.created_by)}
        needsApproval={selectedItem ? needsApproval(selectedItem) : false}
        onApprove={(itemId) => {
          tasks.approveItem.mutate(itemId, {
            onSuccess: () => {
              setSelectedItem(null);
              toast.success("Oppgave godkjent");
            },
          });
        }}
        onEdit={(itemId, updates) => {
          tasks.updateItem.mutate({ itemId, ...updates }, {
            onSuccess: () => {
              setSelectedItem(null);
              toast.success("Oppgave oppdatert");
            },
          });
        }}
        onDelete={(itemId) => {
          tasks.deleteItem.mutate(itemId, {
            onSuccess: () => {
              setSelectedItem(null);
              toast.success("Oppgave slettet");
            },
          });
        }}
        onReset={(itemId) => {
          tasks.resetItem.mutate(itemId, {
            onSuccess: () => {
              setSelectedItem(null);
              toast.success("Oppgaven er nullstilt");
            },
          });
        }}
        onMove={(itemId, newDate) => {
          const previousDate = (selectedItem as any)?.date as string | undefined;
          tasks.updateItem.mutate({ itemId, date: newDate }, {
            onSuccess: () => {
              setSelectedItem(null);
              const newDateLabel = formatDateLong(newDate + "T12:00:00", timezone);
              // Undo støttes via toast action
              toast.success(`Oppgave flyttet til ${newDateLabel}`, {
                duration: 8000,
                action: {
                  label: "Angre",
                  onClick: () => {
                    if (!previousDate) return;
                    tasks.updateItem.mutate(
                      { itemId, date: previousDate },
                      {
                        onSuccess: () => {
                          toast.success("Flytting angret");
                        },
                        onError: () => {
                          toast.error("Kunne ikke angre flytting");
                        },
                      }
                    );
                  },
                },
              });
            },
          });
        }}
        onComplete={(itemId) => {
          setSelectedItem(null);
          const item = items.find((i: any) => i.id === itemId);
          handleComplete(itemId, item?.title || "");
        }}
        familyId={childFamilyId}
        onCategoryChange={(itemId, category) => {
          supabase
            .from("task_items" as any)
            .update({ category } as any)
            .eq("id", itemId)
            .then(({ error }) => {
              if (error) {
                toast.error("Kunne ikke oppdatere kategori");
              } else {
                const updated = tasks.items.find((i: any) => i.id === itemId);
                setSelectedItem(updated ? { ...updated, category } : null);
                toast.success("Kategori oppdatert");
              }
            });
        }}
      />
        </>
      )}

      {/* === Sheets rendered regardless of active tab === */}

      {/* Create task sheet */}
      <TaskItemDetailSheet
        item={null}
        open={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        isParent
        createMode
        familyId={childFamilyId}
        onCreate={(title, durationSeconds, category, description) => {
          tasks.addItem.mutate({ title, durationSeconds, category, description }, {
            onSuccess: () => {
              setShowCreateSheet(false);
              toast.success("Oppgave lagt til");
            },
          });
        }}
        onCreateRecurring={(data) => {
          if (!childFamilyId || !childId) return;
          const rule = data.recurrenceType === "daily"
            ? { type: "daily" as const }
            : { type: "weekly" as const, days: data.weeklyDays.sort() };
          supabase.from("task_templates").insert([{
            child_id: childId,
            family_id: childFamilyId,
            title: data.title,
            description: data.description || null,
            category: data.category || null,
            duration_seconds: data.duration,
            recurrence_rule: rule as any,
            start_date: data.startDate,
            end_date: data.endDate,
          }]).then(({ error }) => {
            if (error) {
              toast.error("Kunne ikke opprette: " + error.message);
            } else {
              setShowCreateSheet(false);
              toast.success("Gjentagende oppgave opprettet");
              queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId] });
              queryClient.invalidateQueries({ queryKey: ["task-templates", childId] });
            }
          });
        }}
        onEdit={() => {}}
        onDelete={() => {}}
        onComplete={() => {}}
      />

      {/* School plan import */}
      <AddHomeworkSheet
        open={showAddHomework}
        onOpenChange={setShowAddHomework}
        childId={childId || ""}
        familyId={childFamilyId || ""}
      />
      <SchoolPlanImport
        open={showPlanImport}
        onClose={() => setShowPlanImport(false)}
        childId={childId || ""}
        childName={child?.name || "Barn"}
        familyId={childFamilyId}
        siblings={siblingOptions}
      />

      {/* Add reminder sheet */}
      {childId && childFamilyId && (
        <AddReminderSheet
          open={showAddReminder}
          onOpenChange={setShowAddReminder}
          childId={childId}
          familyId={childFamilyId}
          allChildren={allChildren}
        />
      )}
    </div>
  );
}

/** Parent timer sub-view */
function ParentTimerView({ view, timer, setView, onPause, onComplete, remainingSecondsRef, durationSeconds }: {
  view: { type: "timer"; itemId: string; title: string; description?: string | null };
  timer: ReturnType<typeof useTimer>;
  setView: (v: ViewState) => void;
  onPause: (itemId: string) => void;
  onComplete: (itemId: string, title: string) => void;
  remainingSecondsRef: React.MutableRefObject<Record<string, number>>;
  durationSeconds: number;
}) {
  useEffect(() => {
    if (timer.isFinished) {
      delete remainingSecondsRef.current[view.itemId];
      setView({ type: "done", itemId: view.itemId, title: view.title });
    }
  }, [timer.isFinished, view.itemId, view.title, setView, remainingSecondsRef]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
      <h2 className="text-xl font-display font-bold text-center">{view.title}</h2>
      {view.description && (
        <p className="text-sm text-muted-foreground bg-muted rounded-lg p-3 max-w-xs text-center whitespace-pre-wrap">
          {view.description}
        </p>
      )}
      <CircularTimer secondsLeft={timer.secondsLeft} progress={timer.progress} />
      <p className="text-sm text-muted-foreground">Varighet: {Math.round(durationSeconds / 60)} minutter 🧘</p>
      <div className="flex gap-3 w-full max-w-xs">
        <Button variant="outline" className="flex-1" onClick={() => onPause(view.itemId)}>
          Pause
        </Button>
        <Button className="flex-1" onClick={() => {
          delete remainingSecondsRef.current[view.itemId];
          timer.reset();
          onComplete(view.itemId, view.title);
        }}>
          Ferdig nå
        </Button>
      </div>
    </div>
  );
}

/** Copy tasks from a sibling's current week – with selective preview */
function CopyFromSiblingButton({
  childId,
  familyId,
  siblings,
  timezone,
}: {
  childId: string;
  familyId: string;
  siblings: { id: string; name: string; avatar_emoji: string }[];
  timezone: string;
}) {
  const [open, setOpen] = useState(false);
  const [selectedSibling, setSelectedSibling] = useState<string>(siblings.length === 1 ? siblings[0].id : "");
  const [siblingTasks, setSiblingTasks] = useState<any[]>([]);
  const [siblingEvents, setSiblingEvents] = useState<any[]>([]);
  const [existingKeys, setExistingKeys] = useState<Set<string>>(new Set());
  const [existingEventKeys, setExistingEventKeys] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const queryClient = useQueryClient();

  const DAY_NAMES = ["Søndag", "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag"];

  function getWeekRange() {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMon);
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return { monStr: getDateString(monday, timezone), friStr: getDateString(friday, timezone) };
  }

  function makeEventKey(e: any) {
    return `${e.event_type}::${e.title}::${e.day_of_week ?? ""}::${e.specific_date ?? ""}`;
  }

  async function loadSiblingTasks(sibId: string) {
    setLoading(true);
    const { monStr, friStr } = getWeekRange();

    const [sibRes, existRes, sibEvRes, existEvRes] = await Promise.all([
      supabase.from("task_items" as any).select("*").eq("child_id", sibId).gte("date", monStr).lte("date", friStr).order("date", { ascending: true }),
      supabase.from("task_items" as any).select("title, date").eq("child_id", childId).gte("date", monStr).lte("date", friStr),
      supabase.from("child_calendar_events" as any).select("*").eq("child_id", sibId),
      supabase.from("child_calendar_events" as any).select("event_type, title, day_of_week, specific_date").eq("child_id", childId),
    ]);

    const tasks = (sibRes.data || []) as any[];
    const keys = new Set((existRes.data || []).map((t: any) => `${t.date}::${t.title}`));
    setSiblingTasks(tasks);
    setExistingKeys(keys);
    setSelectedIds(new Set(tasks.filter((t: any) => !keys.has(`${t.date}::${t.title}`)).map((t: any) => t.id)));

    const events = (sibEvRes.data || []) as any[];
    const evKeys = new Set((existEvRes.data || []).map((e: any) => makeEventKey(e)));
    setSiblingEvents(events);
    setExistingEventKeys(evKeys);
    setSelectedEventIds(new Set(events.filter((e: any) => !evKeys.has(makeEventKey(e))).map((e: any) => e.id)));

    setLoading(false);
  }

  function handleOpen() {
    setOpen(true);
    if (selectedSibling) loadSiblingTasks(selectedSibling);
  }

  function toggleTask(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleCopySelected() {
    setCopying(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id ?? null;
      const tasksToInsert = siblingTasks.filter((t: any) => selectedIds.has(t.id));

      let created = 0;
      for (const task of tasksToInsert) {
        const { error } = await supabase
          .from("task_items" as any)
          .insert({
            child_id: childId,
            family_id: familyId,
            date: task.date,
            title: task.title,
            status: "pending",
            duration_seconds: task.duration_seconds,
            completion_points: task.completion_points,
            created_by: userId,
            category: task.category || null,
            deadline_date: task.deadline_date || null,
          } as any);
        if (!error) created++;
      }

      // Copy homework from sibling
      const { data: sibHomework } = await supabase
        .from("homework" as any)
        .select("*, homework_subtasks(*)")
        .eq("child_id", selectedSibling);
      if (sibHomework && (sibHomework as any[]).length > 0) {
        for (const hw of sibHomework as any[]) {
          // Check duplicate by fingerprint
          const newFingerprint = hw.source_fingerprint.replace(selectedSibling, childId);
          const { data: existing } = await supabase
            .from("homework" as any)
            .select("id")
            .eq("child_id", childId)
            .eq("source_fingerprint", newFingerprint)
            .limit(1);
          if (existing && existing.length > 0) continue;

          const { data: newHw, error: hwErr } = await supabase
            .from("homework" as any)
            .insert({
              child_id: childId,
              family_id: familyId,
              week_number: hw.week_number,
              subject: hw.subject,
              title: hw.title,
              due_date: hw.due_date,
              scope: hw.scope,
              confidence: hw.confidence,
              source_fingerprint: newFingerprint,
              created_by: userId,
            } as any)
            .select("id")
            .single();
          if (!hwErr && newHw) {
            const subtasks = (hw.homework_subtasks || []).map((st: any) => ({
              homework_id: (newHw as any).id,
              title: st.title,
              sort_order: st.sort_order,
              estimated_minutes: st.estimated_minutes,
              requires_upload: st.requires_upload,
            }));
            if (subtasks.length > 0) {
              await supabase.from("homework_subtasks" as any).insert(subtasks as any);
            }
            created++;
          }
        }
      }

      // Copy calendar events
      let eventsCreated = 0;
      const eventsToInsert = siblingEvents.filter((e: any) => selectedEventIds.has(e.id));
      for (const ev of eventsToInsert) {
        const { error } = await supabase
          .from("child_calendar_events" as any)
          .insert({
            child_id: childId,
            family_id: familyId,
            event_type: ev.event_type,
            title: ev.title,
            description: ev.description || null,
            day_of_week: ev.day_of_week,
            specific_date: ev.specific_date || null,
            start_time: ev.start_time || null,
            end_time: ev.end_time || null,
            recurring: ev.recurring,
            source: ev.source || "school_plan_import",
            source_ref: ev.source_ref || null,
            metadata: ev.metadata || {},
            created_by: userId,
          } as any);
        if (!error) eventsCreated++;
      }

      queryClient.invalidateQueries({ queryKey: ["task-items", childId] });
      queryClient.invalidateQueries({ queryKey: ["child-calendar-events", childId] });
      queryClient.invalidateQueries({ queryKey: ["homework", childId] });

      const parts: string[] = [];
      if (created > 0) parts.push(`${created} oppgaver`);
      if (eventsCreated > 0) parts.push(`${eventsCreated} kalenderhendelser`);
      if (parts.length > 0) toast.success(`Kopiert: ${parts.join(" og ")}`);
      else toast.info("Ingen nye elementer ble kopiert");
      setOpen(false);
    } catch {
      toast.error("Kunne ikke kopiere");
    } finally {
      setCopying(false);
    }
  }

  function formatTaskDate(dateStr: string) {
    const d = new Date(dateStr + "T00:00:00");
    return DAY_NAMES[d.getDay()];
  }

  return (
    <>
      <Button
        variant="outline"
        className="w-full rounded-xl text-sm gap-2"
        onClick={handleOpen}
      >
        📋 Kopier ukeplan fra søsken
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="h-[80vh] overflow-y-auto rounded-t-2xl">
          <SheetHeader className="pb-2">
            <SheetTitle className="text-lg font-display">Kopier fra søsken</SheetTitle>
            <SheetDescription>Velg hvilke oppgaver du vil kopiere over</SheetDescription>
          </SheetHeader>

          {/* Sibling picker (if multiple) */}
          {siblings.length > 1 && (
            <div className="mb-4">
              <Select
                value={selectedSibling}
                onValueChange={(v) => {
                  setSelectedSibling(v);
                  loadSiblingTasks(v);
                }}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Velg søsken…" />
                </SelectTrigger>
                <SelectContent>
                  {siblings.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.avatar_emoji} {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {loading && <p className="text-sm text-muted-foreground text-center py-8">Laster oppgaver…</p>}

          {!loading && siblingTasks.length === 0 && siblingEvents.length === 0 && selectedSibling && (
            <p className="text-sm text-muted-foreground text-center py-8">Ingen oppgaver eller hendelser denne uken</p>
          )}

          {!loading && (siblingTasks.length > 0 || siblingEvents.length > 0) && (
            <div className="space-y-4 pb-6">
              {/* Tasks section */}
              {siblingTasks.length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">📝 Oppgaver ({selectedIds.size}/{siblingTasks.length})</span>
                    <Button
                      variant="ghost" size="sm" className="text-xs h-7"
                      onClick={() => {
                        if (selectedIds.size === siblingTasks.length) setSelectedIds(new Set());
                        else setSelectedIds(new Set(siblingTasks.map((t: any) => t.id)));
                      }}
                    >
                      {selectedIds.size === siblingTasks.length ? "Fjern alle" : "Velg alle"}
                    </Button>
                  </div>

                  {siblingTasks.map((task: any) => {
                    const isDuplicate = existingKeys.has(`${task.date}::${task.title}`);
                    return (
                      <Card
                        key={task.id}
                        className={`cursor-pointer transition-colors ${selectedIds.has(task.id) ? "border-primary/50 bg-primary/5" : ""} ${isDuplicate ? "opacity-50" : ""}`}
                        onClick={() => toggleTask(task.id)}
                      >
                        <CardContent className="flex items-center gap-3 p-3">
                          <Checkbox checked={selectedIds.has(task.id)} onCheckedChange={() => toggleTask(task.id)} className="shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{task.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatTaskDate(task.date)} · {Math.round(task.duration_seconds / 60)} min
                              {isDuplicate && " · Finnes allerede"}
                            </p>
                          </div>
                          {task.category && <Badge variant="secondary" className="text-[10px] shrink-0">{task.category}</Badge>}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Calendar events section */}
              {siblingEvents.length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">📅 Kalenderhendelser ({selectedEventIds.size}/{siblingEvents.length})</span>
                    <Button
                      variant="ghost" size="sm" className="text-xs h-7"
                      onClick={() => {
                        if (selectedEventIds.size === siblingEvents.length) setSelectedEventIds(new Set());
                        else setSelectedEventIds(new Set(siblingEvents.map((e: any) => e.id)));
                      }}
                    >
                      {selectedEventIds.size === siblingEvents.length ? "Fjern alle" : "Velg alle"}
                    </Button>
                  </div>

                  {siblingEvents.map((ev: any) => {
                    const isDuplicate = existingEventKeys.has(makeEventKey(ev));
                    const typeLabel = ev.event_type === "school" ? "Timeplan" : ev.event_type === "activity" ? "Aktivitet" : ev.event_type === "deadline" ? "Frist" : "Melding";
                    return (
                      <Card
                        key={ev.id}
                        className={`cursor-pointer transition-colors ${selectedEventIds.has(ev.id) ? "border-primary/50 bg-primary/5" : ""} ${isDuplicate ? "opacity-50" : ""}`}
                        onClick={() => {
                          setSelectedEventIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(ev.id)) next.delete(ev.id);
                            else next.add(ev.id);
                            return next;
                          });
                        }}
                      >
                        <CardContent className="flex items-center gap-3 p-3">
                          <Checkbox
                            checked={selectedEventIds.has(ev.id)}
                            onCheckedChange={() => {
                              setSelectedEventIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(ev.id)) next.delete(ev.id);
                                else next.add(ev.id);
                                return next;
                              });
                            }}
                            className="shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{ev.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {typeLabel}
                              {ev.recurring && ev.day_of_week != null && ` · ${DAY_NAMES[(ev.day_of_week + 1) % 7]}`}
                              {ev.start_time && ` · ${ev.start_time.slice(0, 5)}`}
                              {isDuplicate && " · Finnes allerede"}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Fixed bottom bar */}
          {!loading && (siblingTasks.length > 0 || siblingEvents.length > 0) && (
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
              <Button
                className="w-full rounded-xl"
                disabled={(selectedIds.size + selectedEventIds.size) === 0 || copying}
                onClick={handleCopySelected}
              >
                {copying ? "Kopierer…" : `Kopier ${selectedIds.size + selectedEventIds.size} elementer`}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

/** Read-only preview of what the child sees; used at /parent/child/:id/preview */
export function ParentChildPreview() {
  const { id } = useParams<{ id: string }>();

  // Use child theme so background and gradient match what the child sees (body[data-role="parent"] would use parent colors)
  useEffect(() => {
    document.body.setAttribute("data-role", "child");
    return () => document.body.setAttribute("data-role", "parent");
  }, []);

  return (
    <div
      className="min-h-[100dvh] w-full"
      style={{ background: "hsl(var(--secondary))" }}
    >
      <ChildDayView
        previewMode
        previewBackTo={id ? `/parent/child/${id}` : "/dashboard"}
      />
    </div>
  );
}
